import { ReactComponent as BlueIconInfo } from "@/assets/svgs/blue-icon.svg";
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as IconGreenTick } from "@/assets/svgs/green-tick-badge.svg";
import { ReactComponent as IconInformation } from "@/assets/svgs/i.svg";
import { ReactComponent as IconClose } from "@/assets/svgs/icon-close.svg";
import { ReactComponent as IconReject } from "@/assets/svgs/reject-exclamation.svg";
import { ReactComponent as IconTick } from "@/assets/svgs/tick.svg";
import Button from "@/components/Button/index";
import CardConfigure from "@/componentsDS/Modal/CardConfigure";
import Modal from "@/componentsDS/Modal/Modal";
import Popover from "@/components/Popover";
import Portal from "@/components/Portal";
import Toast from "@/components/Toast";
import useApprovalRequest from "@/hooks/use-approval-request/index";
import useModal from "@/hooks/use-modal/index";
import usePage from "@/hooks/use-page";
import usePersonas from "@/hooks/use-personas";
import useToast from "@/hooks/use-toast";
import useUseCases from "@/hooks/use-use-cases";
import { useLocation, useParams } from "@/lib/router-dom";
import agentEnrolmentApi from "@/services/api/sgtdEnrollmentRequest.js";
import useCaseEnrolmentApi from "@/services/api/adminApprovalRequest";
import rem from "@/utils/rem";
import titleCase from "@/utils/title-case";
import classname from "classnames";
import { produce } from "immer";
import { uniqBy, isEmpty } from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index";
import { useFeature } from "@/hooks/use-feature/index.js";
import { renderUserEmailInline } from '@/utils/formatUserEmail';
import { featureConstant } from "@/constants/configuration/feature";

const Page = () => {
	/*=================================================1
		General
	/================================================*/
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const { currentState } = useLocation();
	const { requestType, requestId, useCaseId } = useParams();
	const pageTitle =
		requestType === "use-case-enrollment"
			? "Use Case Enrolment Request"
			: `${currentState?.requestTypeStatus} Agent - Principal Function Request`;
	const breadcrumbsTitle =
		requestType === "use-case-enrollment"
			? "Use Case Enrolment Request"
			: `${currentState?.requestTypeStatus} Agent Enrolment Request`;
	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: 'Approval Requests',
				link: `/admin/approval-requests`
			},
			{
				label: breadcrumbsTitle,
				link: ``
			},
		]

	});
	const requestTypeStatus = currentState?.requestTypeStatus;

	const toastType = {
		success: "success",
		error: "error",
	};

	const status = {
		pending: "PENDING_APPROVAL",
		approve: "APPROVED",
		reject: "REJECTED",
		revoked: "REVOKED",
	};

	const actionType = {
		approve: "APPROVE",
		reject: "REJECT",
	};

	const [submitLoading, setSubmitLoading] = useState();

	/*=================================================
		Modal
	//================================================*/
	const {
		visible: visibleConfirmation,
		show: showConfirmation,
		hide: hideConfirmation,
	} = useModal();
	const [action, setAction] = useState("");

	const [rejectReason, setRejectReason] = useState("");

	/*=================================================
	  API
	/================================================*/

	const { toastList, addToastMessage } = useToast();
	const { items: personaList, fetch: fetchPersonaList } = usePersonas();
	const {
		findApprovalRequests: getRequest,
		currentItemApprovalRequests: requestItem,
		errorApprovalRequests: errorRequestList,
		personaItems,
		setPersonaItems,
	} = useApprovalRequest();

	const {
		find: findUseCase,
		currentItem: useCaseItem,
		setCurrentItem: setUseCaseItem,
	} = useUseCases();

	const getData = {
		useCaseId: useCaseId,
	};

	useEffect(() => {
		getRequest(requestId);
		fetchPersonaList(getData);
	}, []);

	useEffect(() => {
		async function getData() {
			const getUseCase = {
				useCaseId: useCaseId,
				organizationId: requestItem?.orgId,
			};

			const selectedUseCaseItem = await findUseCase(getUseCase);
			if (selectedUseCaseItem && selectedUseCaseItem.length > 0) {
				setUseCaseItem(selectedUseCaseItem[0]);
			}
		}
		if(!isEmpty(requestItem)){
			getData();
		}
	}, [requestItem]);

	useEffect(() => {
		addToastMessage(errorRequestList, toastType.error);
	}, [errorRequestList]);

	/*=================================================
	  Persona Selection
	/================================================*/

	const [personaActive, setPersonaActive] = useState(false);
	const [personaPlaceHolder, setPersonaPlaceHolder] =
		useState("Select Persona(s)");
	const [newPersona, setNewPersona] = useState({});

	const personaRef = useRef(null);

	useEffect(() => {
		document.addEventListener("mousedown", handlePersonaClick);
		return () => {
			document.removeEventListener("mousedown", handlePersonaClick);
		};
	}, []);

	function handlePersonaClick(event) {
		if (!personaRef.current) {
			return;
		}

		if (personaRef.current.contains(event.target)) {
			return;
		}

		setPersonaActive(false);
	}

	const UseCaseEnrolmentPersona = () => {
		const dropdownBtnClass = classname({
			"enrolment-request__dropdown-btn":
				requestItem?.status === status.pending ? true : false,
			"enrolment-request__dropdown-btn--disabled":
				requestItem?.status === status.pending ? false : true,
		});

		function handleSelected(option) {
			setPersonaPlaceHolder(option.name);
			setNewPersona({
				id: option.id,
				name: option.name,
				$selected: true,
			});
			setPersonaActive(false);
		}

		function addPersona() {
			if (personaPlaceHolder === "Select Persona(s)") {
				return;
			}

			const uniqPersona = uniqBy([...personaItems, newPersona], "id");
			setPersonaItems(uniqPersona);
			setPersonaPlaceHolder("Select Persona(s)");
		}

		const handleUpdatePersonaSelect = (item) => {
			if (requestItem?.status !== status.pending) {
				return;
			}

			const index = personaItems?.findIndex(
				(persona) => persona.id === item.id
			);
			const updatedItems = produce(personaItems, (draft) => {
				draft[index].$selected = !draft[index].$selected;
			});

			setPersonaItems(updatedItems);
		};

		const Checkbox = ({ selected, handleUpdateItemSelect, disabled }) => {
			const personaCheckBoxClass = classname({
				"use-case-request__checkbox": true,
				"use-case-request__checkbox--active": selected,
				"use-case-request__checkbox--disabled": disabled,
			});

			return (
				<div
					data-testid="checkbox-element"
					className={personaCheckBoxClass}
					onClick={handleUpdateItemSelect}
					onKeyDown={(e) => {
						if (e.key === "Enter") handleUpdateItemSelect();
					}}
				>
					{selected ? <IconTick /> : null}
				</div>
			);
		};

		return (
			<>
				<div className="enrolment-request__title">
					Selected Personas
				</div>
				<div className="enrolment-request__title--small">
					Add or remove relevant personas for the company
				</div>
				<div className="enrolment-request__selection-container">
					{personaItems?.map((item, index) => {
						return (
							<div
								className="use-case-request__selection"
								key={index}
							>
								<Checkbox
									selected={item.$selected}
									handleUpdateItemSelect={() =>
										handleUpdatePersonaSelect(item)
									}
									disabled={
										!(
											requestItem?.status ===
											status.pending
										)
									}
								/>
								<div className="enrolment-request__desc">
									{item.name}
								</div>
							</div>
						);
					})}
				</div>
				<div
					className="enrolment-request__dropdown-container"
					ref={personaRef}
				>
					<Popover visible={personaActive}>
						<Popover.Trigger>
							<div className="enrolment-request__dropdown">
								<div className="enrolment-request__dropdown-placeholder">
									{personaPlaceHolder}
								</div>
								<div
									data-testid="persona-dropdown"
									className={dropdownBtnClass}
									onClick={() =>
										requestItem?.status === status.pending
											? setPersonaActive(!personaActive)
											: setPersonaActive(false)
									}
									onKeyDown={(e) => {
										if (
											e.key === "Enter" &&
											requestItem?.status ===
												status.pending
										) {
											setPersonaActive(!personaActive);
										} else {
											setPersonaActive(false);
										}
									}}
								>
									<IconChevronDown />
								</div>
							</div>
						</Popover.Trigger>
						<Popover.Content>
							<div className="enrolment-request__dropdown-content ">
								{personaList.map((option, index) => (
									<div
										className="enrolment-request__dropdown-item"
										key={option.id}
										onClick={() => {
											handleSelected(option);
										}}
										onKeyDown={(e) => {
											if (e.key === "Enter")
												handleSelected(option);
										}}
									>
										{option.name}
									</div>
								))}
							</div>
						</Popover.Content>
					</Popover>
					<Button
						full
						maxWidth="60"
						text="Add"
						disabled={!(requestItem?.status === status.pending)}
						submit={() => addPersona()}
					/>
				</div>
			</>
		);
	};

	const AgentPrincipleEnrolment = ({ requestStatus = "Enable" }) => {
		return (
			(requestItem?.organizationUseCases?.length !== 0 ||
				requestType === "agent") && (
				<>
					<div className="use_agent_title">
						Agent - Principle Function
					</div>
					<div className="agent-use-case-request__back">
						<div className="card-configure__icon enrolment-request__card-icon">
							<BlueIconInfo className="enrolment-request__icon" />
						</div>
						<div className="enrolment-request__title--small">{`${requestStatus} Agent - Principal Function`}</div>
					</div>
				</>
			)
		);
	};

	const ReviewDetails = () => {
		return (
			<div className="use-case-request__subheader">
				{requestType === "use-case-enrollment" ? (
					<>{`User has submitted a request for ${requestItem?.user?.organization?.name} to join `}</>
				) : requestTypeStatus === "Enable" ? (
					<>{`User has submitted a request which is enrol agent principle in `}</>
				) : (
					<>{`User has submitted a request which is disabled agent principle in `}</>
				)}
				<strong>{useCaseItem?.name}. </strong>
				Please review the details:
			</div>
		);
	};

	/*=================================================
		API Action Functions
	/================================================*/

	async function requestAction() {
		if (submitLoading) {
			return;
		}

		setSubmitLoading(true);

		let payload, error, result;
		if (action === actionType.approve) {
			if (requestType === "use-case-enrollment") {
				const enrolledPersona = personaItems
					?.filter((persona) => persona.$selected === true)
					.map((persona) => {
						return persona.id;
					});

				payload = [
					{
						adminRequestId: requestId,
						personaIds: enrolledPersona,
					},
				];
			} else {
				payload = [
					{
						adminRequestId: requestId,
						personaIds: [],
						comment: "",
					},
				];
			}

			setSubmitLoading(false);

			if (requestType === "use-case-enrollment") {
				[error, result] = await useCaseEnrolmentApi.approveRequest(
					payload
				);
			} else {
				[error, result] = await agentEnrolmentApi.approveOrRejectAgent(
					"approve",
					payload
				);
			}

			if (error) {
				return addToastMessage(error?.message, toastType.error);
			}

			addToastMessage(
				"You have successfully approved the request.",
				toastType.success
			);
		}

		if (action === actionType.reject) {
			if (requestType === "use-case-enrollment") {
				payload = [
					{
						adminRequestId: requestId,
						comment: rejectReason,
					},
				];
			} else {
				payload = [
					{
						adminRequestId: requestId,
						personaIds: [],
						comment: rejectReason.length > 0 ? rejectReason : " ",
					},
				];
			}

			setSubmitLoading(false);

			if (requestType === "use-case-enrollment") {
				[error, result] = await useCaseEnrolmentApi.rejectRequest(
					payload
				);
			} else {
				[error, result] = await agentEnrolmentApi.approveOrRejectAgent(
					"reject",
					payload
				);
			}

			if (error) {
				return addToastMessage(error?.message, toastType.error);
			}

			setRejectReason("");
			addToastMessage(
				"You have rejected the request.",
				toastType.success
			);
		}

		getRequest(requestId);
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>
			</div>
			<hr />

			<div className="layout-data-elements">
				<div className="layout-data-elements__left">
					<div className="layout-data-elements__block">
						<div className="use-case-request-left__header">
							<div className="use-case-request-left__content">
								<div
									className="use-case-request-left__content-icon"
									style={{
										backgroundImage: `url(${requestItem?.user?.organization?.iconUrl})`,
									}}
								></div>
								<div className="use-case-request-left__content-desc">
									<div className="enrolment-request__title">
										{requestItem?.user?.organization?.name}
									</div>
									<div className="enrolment-request__title--normal">
										{moment(requestItem?.created).format(
											"D MMM YYYY, h:mm A"
										)}
									</div>
								</div>
							</div>
							<div className="use-case-request-left__action-btn">
								{requestItem?.status === status.pending && (
									<>
										<Button
											full
											maxWidth="90"
											addClass={
												"use-case-request-left__right-action-btn"
											}
											text="Approve"
											submit={() => {
												setAction("APPROVE");
												showConfirmation();
											}}
										/>
										<Button
											text="Reject"
											reject
											submit={() => {
												setAction("REJECT");
												setRejectReason("");
												showConfirmation();
											}}
										/>
									</>
								)}
							</div>
						</div>

						<ReviewDetails />

						<div className="">
							{requestType === "use-case-enrollment" && (
								<UseCaseEnrolmentPersona />
							)}
							<AgentPrincipleEnrolment
								requestStatus={requestTypeStatus}
							/>
						</div>
					</div>
					<div className="enrolment-request__block">
						<div className="use-case-request-left__bottom-header">
							<div className="enrolment-request__text">
								<div className="enrolment-request__title">
									Request History
								</div>
							</div>
						</div>
						<div className="use-case-request-left__block-container">
							{requestItem?.status === status.reject && (
								<div className="use-case-request-left__history-container">
									<IconReject />
									<div className="use-case-request-left__bottom-content">
										<div className="enrolment-request__title--normal-bold">
											Request Rejected
										</div>
										<div className="enrolment-request__title--small">
											by {isSoftDeleteUserEnabled ? renderUserEmailInline(requestItem?.modifiedBy) : requestItem?.modifiedBy?.email} on {moment(requestItem?.modified).format("D MMM YYYY, h:mm A")}
										</div>
										<div className="use-case-request-left__history-desc">{`Reason for rejection: ${requestItem?.comment}`}</div>
									</div>
								</div>
							)}
							{requestItem?.status === status.approve && (
								<div className="use-case-request-left__history-container">
									<IconGreenTick />
									<div className="use-case-request-left__bottom-content">
										<div className="enrolment-request__title--normal-bold">
											Request Approved
										</div>
										<div className="enrolment-request__title--small">
											by {isSoftDeleteUserEnabled ? renderUserEmailInline(requestItem?.modifiedBy) : requestItem?.modifiedBy?.email} on {moment(requestItem?.modified).format("D MMM YYYY, h:mm A")}
										</div>
									</div>
								</div>
							)}
							<div className="use-case-request-left__history-container">
								<IconGreenTick />
								<div className="use-case-request-left__bottom-content">
									<div className="enrolment-request__title--normal-bold">
										Request Submitted
									</div>
									<div className="enrolment-request__title--small">
										by {isSoftDeleteUserEnabled ? renderUserEmailInline(requestItem?.user) : requestItem?.user?.email} on {moment(requestItem?.created).format("D MMM YYYY, h:mm A")}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="layout-data-elements__right">
					<div className="layout-data-elements__block">
						<div className="enrolment-request__title--small">
							Use Case
						</div>
						<div className="use-case-request-right__title">
							{useCaseItem?.name}
						</div>
						<div className="use-case-request-right__desc">
							{useCaseItem?.description}
						</div>
					</div>
				</div>
			</div>

			<Modal visible={visibleConfirmation} variant="medium" close={hideConfirmation}>
				<CardConfigure>
					<CardConfigure.Header>
						<CardConfigure.Header.Title>
							<div className="card-configure__header__left">
								<div className="card-configure__title">
									<div className="card-configure__title__left">
										<IconInformation />
									</div>
									<div className="card-configure__title__right">
										<div className="card-configure__title__right__top">
											{titleCase(action)} Request
										</div>
									</div>
								</div>
							</div>
						</CardConfigure.Header.Title>
					</CardConfigure.Header>
					<CardConfigure.Body>
						<CardConfigure.Body.Col>
							{action === actionType.approve && (
								<div className="action-modal__text">
									Are you sure you want to proceed to approve
									this request?
								</div>
							)}
							{action === actionType.reject && (
								<>
									<div className="action-modal__text">
										Are you sure you want to reject this
										request? The requestor will be notified.
									</div>
									<textarea
										className="action-modal__rejection-textarea"
										placeholder="Reason for rejection (Optional)"
										onChange={(e) =>
											setRejectReason(e.target.value)
										}
									></textarea>
								</>
							)}
						</CardConfigure.Body.Col>
					</CardConfigure.Body>
					<CardConfigure.Footer>
						<CardConfigure.Footer.Right>
							<Button
								full
								maxWidth="78"
								text={
									action === actionType.approve
										? "Confirm"
										: "Reject"
								}
								submit={() => {
									hideConfirmation();
									requestAction();
								}}
							/>
						</CardConfigure.Footer.Right>
					</CardConfigure.Footer>
				</CardConfigure>
			</Modal>

			{submitLoading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/img/loading.gif")} />
						</div>
					</div>,
					document.body
				)}

			<Toast toastList={toastList} />
		</>
	);
};

export default Page;
